'use client';

import { createContext, useMemo, type ReactNode } from 'react';

import { cn } from '@/components/ui/utils';
import { guestV2PaletteVars } from '@/components/guest-v2/palette';
import { useTheme } from '@/contexts/ThemeProvider';
import { branchPageThemeStyle, type BranchPageTheme } from '@/lib/menu-themes/branch-page-theme';
import type { CSSProperties } from 'react';

/** The active branch palette for Radix portals, which render outside this scope. */
export const BranchPaletteContext = createContext<CSSProperties | undefined>(undefined);

/** Keeps a public branch's selected template/theme palette scoped to its routes. */
export function BranchThemeScope({
  theme,
  children,
  className,
}: {
  theme: BranchPageTheme | null | undefined;
  children: ReactNode;
  className?: string;
}) {
  if (!theme) return children;
  return <ThemedBranchScope theme={theme} className={className}>{children}</ThemedBranchScope>;
}

function ThemedBranchScope({
  theme,
  children,
  className,
}: {
  theme: BranchPageTheme;
  children: ReactNode;
  className?: string;
}) {
  const { theme: mode } = useTheme();
  const palette = theme[mode === 'dark' ? 'dark' : 'light'];
  const portalPalette = useMemo(() => guestV2PaletteVars(palette, mode === 'dark' ? 'dark' : 'light'), [palette, mode]);
  return (
    <BranchPaletteContext.Provider value={portalPalette}>
      <div data-branch-theme-scope="" className={cn('branch-theme-scope', className)} style={branchPageThemeStyle(theme)}>
        {children}
      </div>
    </BranchPaletteContext.Provider>
  );
}
